import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { ColumnDefinition, ExportMetadata } from './exportToCsv'
import i18n from '../i18n'

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable?: {
      finalY: number
    }
  }
}

/**
 * Export table data to PDF file
 * @param data - Array of data objects to export
 * @param columns - Column definitions with keys, labels, and optional formatters
 * @param metadata - Metadata including title, date range, and filters
 */
export function exportToPdf(
  data: any[],
  columns: ColumnDefinition[],
  metadata: ExportMetadata
): void {
  if (!data || data.length === 0) {
    console.warn('No data to export')
    return
  }

  // Create new PDF document
  const doc = new jsPDF({
    orientation: columns.length > 6 ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  // Add "bilpriser.no PROFF" branding top-right
  const pageWidth = doc.internal.pageSize.getWidth()
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(59, 130, 246) // Blue-500
  doc.text('bilpriser.no', pageWidth - 14, 10, { align: 'right' })
  doc.setFontSize(9)
  doc.setTextColor(107, 114, 128) // Gray-500
  doc.text('PROFF', pageWidth - 14, 15, { align: 'right' })
  doc.setTextColor(0, 0, 0) // Reset to black

  // Add title
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.text(metadata.title, 14, 15)

  let yPosition = 22

  // Add metadata info
  if (metadata.dateRange || metadata.filters) {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')

    if (metadata.dateRange) {
      doc.text(i18n.t('export.pdfPeriod', { range: metadata.dateRange }), 14, yPosition)
      yPosition += 5
    }

    if (metadata.filters && metadata.filters.length > 0) {
      metadata.filters.forEach((filter) => {
        doc.text(filter, 14, yPosition)
        yPosition += 5
      })
    }

    yPosition += 2
  }

  // Add explanation text (query parameter summary)
  if (metadata.explanationText) {
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    const maxWidth = doc.internal.pageSize.getWidth() - 28 // 14mm margins on each side
    const lines = doc.splitTextToSize(metadata.explanationText, maxWidth)
    lines.forEach((line: string) => {
      doc.text(line, 14, yPosition)
      yPosition += 4
    })
    yPosition += 2
  }

  // Add chart image if provided (e.g. from AgeAnalyticsBrandDetail)
  if (metadata.chartImage) {
    try {
      const imgProps = doc.getImageProperties(metadata.chartImage)
      const maxImgWidth = pageWidth - 28
      const imgWidth = Math.min(maxImgWidth, 160)
      const imgHeight = (imgProps.height / imgProps.width) * imgWidth
      doc.addImage(metadata.chartImage, 'PNG', 14, yPosition, imgWidth, imgHeight)
      yPosition += imgHeight + 6
    } catch {
      // Chart image rendering failed; skip and continue
    }
  }

  // Sanitize formatted strings for jsPDF compatibility
  // Norwegian locale uses U+2212 (MINUS SIGN) and U+00A0 (NO-BREAK SPACE)
  // which jsPDF's default fonts can't render correctly
  const sanitizeForPdf = (text: string): string => {
    return text
      .replace(/\u2212/g, '-')   // Replace Unicode minus sign with ASCII hyphen-minus
      .replace(/\u00A0/g, ' ')   // Replace non-breaking space with regular space
      .replace(/\u2013/g, '-')   // Replace en-dash with hyphen
      .replace(/\u2014/g, '-')   // Replace em-dash with hyphen
      .replace(/\uFFFD/g, '')    // Remove Unicode replacement character (corrupted bytes)
  }

  // Transform data for table
  const tableData = data.map((row) => {
    return columns.map((col) => {
      const value = row[col.key]
      const formatted = col.format ? col.format(value, row) : value?.toString() || ''
      return sanitizeForPdf(String(formatted))
    })
  })

  // Check if first row is TOTAL row
  const firstRow = data[0]
  const isTotalRow = firstRow && (
    firstRow.brand === 'TOTAL' || 
    firstRow.master_model === 'ALL MODELS' || 
    firstRow.market_share === null
  )

  // Generate table
  autoTable(doc, {
    head: [columns.map((col) => col.label)],
    body: tableData,
    startY: yPosition,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [59, 130, 246], // Blue-500
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      // Right-align numeric columns (assuming columns with numbers/percentages)
      ...Object.fromEntries(
        columns.map((col, index) => {
          const shouldRightAlign = col.key.includes('count') || 
                                   col.key.includes('percentage') || 
                                   col.key.includes('share') ||
                                   col.key.includes('registrations')
          return [index, { halign: shouldRightAlign ? 'right' : 'left' }]
        })
      ),
    },
    didParseCell: function (data) {
      // Style TOTAL row if it's the first row
      if (isTotalRow && data.row.index === 0) {
        data.cell.styles.fillColor = [219, 234, 254] // Blue-100
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.textColor = [30, 64, 175] // Blue-800
      }
    },
  })

  // Add footer with timestamp
  const pageCount = doc.getNumberOfPages()
  const timestamp = new Date().toLocaleString('nb-NO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.text(
      i18n.t('export.pdfFooter', { page: i, total: pageCount, timestamp }),
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    )
  }

  // Generate filename
  const dateStamp = new Date().toISOString().split('T')[0]
  const sanitizedTitle = metadata.title.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '_')
  const filename = `${sanitizedTitle}_${dateStamp}.pdf`

  // Save PDF
  doc.save(filename)
}
